import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import {
  collection, onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import dayjs from 'dayjs';
import { db, functions } from '../../firebase-config';
import { getCollectionDocs } from '../api/document-api/DocumentAPI';
import { OptionType } from '../api/types/AssociationTypes';
import { Container } from '../helper';
import VendorConfigModal, { CostCategory, ProcessedVendor } from './modals/VendorConfigModal';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import { VendorConfig } from './types/VendorConfigTypes';

function VendorRulesPage() {
  const [vendorConfigs, setVendorConfigs] = useState<VendorConfig[]>([]);
  const [vendors, setVendors] = useState<ProcessedVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<VendorConfig | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [selectedChargeTypes, setSelectedChargeTypes] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<VendorConfig | null>(null);
  const [costCategories, setCostCategories] = useState<CostCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const vendorsData = await getCollectionDocs('engine/payee/associations') as OptionType[];

        // Post-process vendors data to extract vendor name and vendor ID
        const processedVendors = vendorsData.map((vendor) => {
          // Extract vendor ID (first item when splitting value by hyphen)
          const vendorId = vendor.value.split('-')[0];

          // Extract vendor name from label
          // Label format: "Vendor Name (code) (location)" or "Vendor Name (location)"
          let vendorName = vendor.label;

          // Remove everything after the first opening parenthesis
          const firstParenIndex = vendorName.indexOf('(');
          if (firstParenIndex !== -1) {
            vendorName = vendorName.substring(0, firstParenIndex).trim();
          }

          return {
            label: vendorName,
            value: vendorId,
            vendorId,
          };
        });

        // Remove duplicates based on vendorId and label, keeping the first occurrence
        const uniqueVendors = processedVendors.filter((vendor, index, self) => {
          const firstIndex = self.findIndex((v) => v.vendorId === vendor.vendorId || v.label === vendor.label);
          return index === firstIndex;
        });

        setVendors(uniqueVendors);
      } catch (error) {
        console.error('Error loading vendors:', error);
        enqueueSnackbar('Error loading vendors', { variant: 'error' });
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const fetchCostCategories = async () => {
      setLoadingCategories(true);
      try {
        const getCostCategories = httpsCallable(functions, 'getCostCategories');
        const result = await getCostCategories();
        const data = result.data as { [key: string]: CostCategory };
        setCostCategories(Object.values(data));
      } catch (error) {
        console.error('Error fetching cost categories:', error);
        enqueueSnackbar('Error loading cost categories', { variant: 'error' });
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCostCategories();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'vendorConfigs'), (snapshot) => {
      const configs: VendorConfig[] = [];
      snapshot.forEach((docSnapshot) => {
        configs.push({ id: docSnapshot.id, ...docSnapshot.data() } as VendorConfig);
      });
      setVendorConfigs(configs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddNew = () => {
    setEditingConfig(null);
    setSelectedVendor('');
    setSelectedChargeTypes([]);
    setDialogOpen(true);
  };

  const handleEdit = (config: VendorConfig) => {
    setEditingConfig(config);
    // Find the vendor by vendorId for the autocomplete
    const vendor = vendors.find((v) => v.vendorId === config.vendorId);
    setSelectedVendor(vendor ? vendor.vendorId : '');
    setSelectedChargeTypes(config.allowedChargeTypes);
    setDialogOpen(true);
  };

  const handleDeleteClick = (config: VendorConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const handleVendorChange = (vendor: string) => {
    setSelectedVendor(vendor);
  };

  const handleChargeTypesChange = (chargeTypes: string[]) => {
    setSelectedChargeTypes(chargeTypes);
  };

  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'vendorName',
      headerName: 'Vendor Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'allowedChargeTypes',
      headerName: 'Allowed Utility Charge Types',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<VendorConfig>) => (
        <Box
          display="flex"
          gap={0.5}
          flexWrap="wrap"
          alignItems="center"
          height="100%"
        >
          {params.row.allowedChargeTypes
            .map((chargeType: string) => {
              const category = costCategories.find((cat) => cat.id === chargeType);
              return {
                chargeType,
                label: category?.label || chargeType,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ chargeType, label }) => (
              <Chip
                key={chargeType}
                label={label}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'var(--westland-red)',
                  color: 'var(--westland-red)',
                }}
              />
            ))}
        </Box>
      ),
    },
    {
      field: 'lastUpdatedBy',
      headerName: 'Last Updated By',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<VendorConfig>) => {
        const { lastUpdatedBy } = params.row;
        if (!lastUpdatedBy) {
          return <div>Not available</div>;
        }
        return (
          <div>
            {lastUpdatedBy.name}
          </div>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated At',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<VendorConfig>) => {
        const { updatedAt } = params.row;
        if (!updatedAt) {
          return <div>Not available</div>;
        }
        return (
          <div>
            {dayjs.unix(updatedAt.seconds).format('M/D/YYYY hh:mm A')}
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<VendorConfig>) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ color: 'var(--westland-red)' }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredVendorConfigs = vendorConfigs.filter((config) => {
    if (!searchTerm) return true;
    const vendor = vendors.find((v) => v.vendorId === config.vendorId);
    return vendor?.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Loading vendor configurations...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Vendor Rules
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              backgroundColor: 'var(--westland-red)',
              '&:hover': {
                backgroundColor: 'var(--westland-red-darker)',
              },
            }}
          >
            Add Vendor Config
          </Button>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vendor Configurations
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure which utility charge types are allowed for each vendor.
              These apply only to single utility invoices.
            </Typography>

            <Box sx={{ height: 520, width: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search by vendor name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />
              </Box>
              <DataGridPro
                rows={filteredVendorConfigs}
                columns={columns}
                loading={loading}
                getRowId={(row: VendorConfig) => row.id || ''}
                pagination
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 25 },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <VendorConfigModal
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          editingConfig={editingConfig}
          vendors={vendors}
          selectedVendor={selectedVendor}
          selectedChargeTypes={selectedChargeTypes}
          onVendorChange={handleVendorChange}
          onChargeTypesChange={handleChargeTypesChange}
          onSave={() => {}}
          costCategories={costCategories}
          loadingCategories={loadingCategories}
          existingConfigs={vendorConfigs}
        />

        <DeleteConfirmationModal
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          configToDelete={configToDelete}
          onDeleteSuccess={handleDeleteSuccess}
        />
      </Box>
    </Container>
  );
}

export default VendorRulesPage;
